/**
 * <rz-facets data-section-id>: filters, sorting and paging for collection and search results
 * without full page reloads. Without JS the filter form is a plain GET form and still works.
 *
 * Every change builds a URL from the filter form, fetches just this section through the
 * Section Rendering API, and swaps the parts marked with [data-facets-part="name"].
 */
if (!customElements.get('rz-facets')) {
  customElements.define(
    'rz-facets',
    class extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;

        this.addEventListener('change', (event) => {
          // Sort radios in the mobile filter drawer drive the toolbar select, the one field that submits sort_by.
          if (event.target.matches('[data-sort-option]')) {
            const select = this.querySelector('select[name="sort_by"]');
            if (select) select.value = event.target.value;
          }
          if (event.target.closest('[data-facets-form]') || event.target.form?.matches('[data-facets-form]')) {
            // One request after the clicks settle (as in Ultra), not one per tick. A request already on its way is
            // cancelled now: painting it would overwrite the tick the visitor just made.
            this.controller?.abort();
            clearTimeout(this.debounce);
            this.debounce = setTimeout(() => this.renderFromForm(), event.target.type === 'number' ? 600 : 500);
          }
        });
        this.addEventListener('submit', (event) => {
          if (!event.target.matches('[data-facets-form]')) return;
          event.preventDefault();
          this.renderFromForm();
        });
        this.addEventListener('click', (event) => this.onClick(event));
        this.addEventListener('input', (event) => this.syncPriceRange(event));

        this.restoreLayout();
        this.restoreLoaded();
        this.observeInfinite();
        this.watchStickyToolbar();
        window.addEventListener('popstate', () => this.render(location.href, false));
        // Leaving for a product after "Load more": keep the scroll position for restoreLoaded.
        window.addEventListener('pagehide', () => history.state?.rzFrom && history.replaceState({ ...history.state, rzScroll: scrollY }, ''));
      }

      // Sticky toolbar (phones/tablets): stuck once its place has scrolled under the header, while the results are
      // still on screen. Bottom mode is fixed, so the sentinel keeps the toolbar's height in the page meanwhile.
      watchStickyToolbar() {
        const toolbar = this.querySelector('[data-sticky-toolbar]');
        const sentinel = this.querySelector('[data-toolbar-sentinel]');
        if (!toolbar || !sentinel) return;
        const bottom = toolbar.dataset.stickyToolbar === 'bottom';
        const mobile = matchMedia('(max-width: 989px)');
        let frame;
        const update = () => {
          const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
          const past = sentinel.getBoundingClientRect().top < header;
          const inResults = this.getBoundingClientRect().bottom > (bottom ? innerHeight : header + toolbar.offsetHeight);
          const stuck = mobile.matches && past && inResults;
          if (stuck === toolbar.classList.contains('is-stuck')) return;
          sentinel.style.height = stuck && bottom ? `${toolbar.offsetHeight}px` : '';
          toolbar.classList.toggle('is-stuck', stuck);
        };
        const schedule = () => {
          cancelAnimationFrame(frame);
          frame = requestAnimationFrame(update);
        };
        addEventListener('scroll', schedule, { passive: true });
        addEventListener('resize', schedule);
        update();
      }

      get form() {
        return this.querySelector('[data-facets-form]');
      }

      renderFromForm() {
        const params = new URLSearchParams(new FormData(this.form));
        // Drop empty price bounds so they don't count as active filters.
        for (const [key, value] of [...params]) if (value === '') params.delete(key);
        this.render(`${location.pathname}?${params}`);
      }

      onClick(event) {
        const remove = event.target.closest('[data-facets-remove]');
        if (remove) {
          event.preventDefault();
          this.render(remove.href);
          return;
        }

        const more = event.target.closest('[data-load-more]');
        if (more) {
          event.preventDefault();
          this.loadMore(more);
          return;
        }

        const showAll = event.target.closest('[data-show-all]');
        if (showAll) {
          this.expandFacet(showAll.closest('.facet'));
          return;
        }

        const toggle = event.target.closest('[data-filters-toggle]');
        if (toggle && matchMedia('(min-width: 990px)').matches) {
          event.preventDefault();
          event.stopPropagation();
          const hidden = this.classList.toggle('facets--hidden');
          toggle.setAttribute('aria-expanded', String(!hidden));
          return;
        }

        const layout = event.target.closest('[data-layout]');
        if (layout) this.setLayout(layout.dataset.layout);
      }

      async fetchSection(url, signal) {
        const sectionUrl = `${url}${url.includes('?') ? '&' : '?'}section_id=${this.sectionId}`;
        const response = await fetch(sectionUrl, { signal });
        if (!response.ok) throw new Error(response.status);
        return new DOMParser().parseFromString(await response.text(), 'text/html');
      }

      async render(url, push = true) {
        // Only the latest change may paint: the previous request is cancelled (as in Ultra), so an older,
        // slower response can never land last and bring back filters that were just unticked.
        clearTimeout(this.debounce);
        this.controller?.abort();
        const controller = (this.controller = new AbortController());
        this.setAttribute('aria-busy', 'true');
        try {
          let doc;
          try {
            doc = await this.fetchSection(url, controller.signal);
          } catch (error) {
            if (controller.signal.aborted) return;
            throw error;
          }
          if (controller.signal.aborted) return;
          const openFacets = [...this.querySelectorAll('details.facet[open]')].map((d) => d.dataset.facet);
          const expandedFacets = [...this.querySelectorAll('details.facet--all')].map((d) => d.dataset.facet);
          const focusedId = document.activeElement?.id;

          doc.querySelectorAll('[data-facets-part]').forEach((fresh) => {
            const current = this.querySelector(`[data-facets-part="${fresh.dataset.facetsPart}"]`);
            if (current) current.replaceWith(fresh);
          });

          this.querySelectorAll('details.facet').forEach((d) => {
            if (openFacets.includes(d.dataset.facet)) d.open = true;
            // Keep "Show more" lists expanded after the filters re-render.
            if (expandedFacets.includes(d.dataset.facet)) this.expandFacet(d);
          });
          if (focusedId) document.getElementById(focusedId)?.focus({ preventScroll: true });

          if (push) history.pushState({}, '', url);
          this.restoreLayout();
          this.observeInfinite();

          // Scrolled past the top of the results: bring it back into view below the sticky header.
          const results = this.querySelector('[data-facets-part="results"]');
          const top = results?.getBoundingClientRect().top;
          if (top < 0) {
            const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
            scrollTo({ top: top + scrollY - header - 16, behavior: 'smooth' });
          }
        } finally {
          if (this.controller === controller) this.removeAttribute('aria-busy');
        }
      }

      // "Show more": the values past the cut-off come out of their <template> (snippets/facets.liquid).
      expandFacet(facet) {
        facet.querySelectorAll('template[data-facet-extra]').forEach((tmpl) => tmpl.replaceWith(tmpl.content));
        facet.classList.add('facet--all');
        facet.querySelector('[data-show-all]')?.remove();
      }

      async loadMore(button) {
        if (button.disabled) return;
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        let doc;
        try {
          doc = await this.fetchSection(button.href);
        } catch (e) {
          // Network error or a failed response: the button is a real link to the next page, so just go there.
          location.href = button.href;
          return;
        }
        const grid = this.querySelector('[data-facets-grid]');
        doc.querySelectorAll('[data-facets-grid] > li:not([data-promo])').forEach((item) => grid.append(item));
        const freshPaging = doc.querySelector('[data-facets-part="pagination"]');
        this.querySelector('[data-facets-part="pagination"]')?.replaceWith(freshPaging);
        // Remember where loading started, so "back" from a product can rebuild the list (restoreLoaded).
        const from = history.state?.rzFrom || Number(new URL(location.href).searchParams.get('page') || 1);
        history.replaceState({ ...history.state, rzFrom: from }, '', button.href);
        this.observeInfinite();
      }

      // Back from a product after "Load more": the browser reloads ?page=N, which holds only that page.
      // Put back the pages loaded before it, then the scroll position (both kept in history.state).
      async restoreLoaded() {
        const state = history.state || {};
        const url = new URL(location.href);
        const page = Number(url.searchParams.get('page') || 1);
        if (!state.rzFrom || page <= state.rzFrom) return;
        history.scrollRestoration = 'manual';
        const grid = this.querySelector('[data-facets-grid]');
        const first = grid?.firstElementChild;
        try {
          for (let p = state.rzFrom; p < page; p++) {
            url.searchParams.set('page', p);
            const doc = await this.fetchSection(url.href);
            doc.querySelectorAll('[data-facets-grid] > li:not([data-promo])').forEach((item) => grid.insertBefore(item, first));
          }
        } catch (e) {
          // Network error: the visitor keeps the page they came back to.
        }
        if (state.rzScroll) scrollTo(0, state.rzScroll);
      }

      observeInfinite() {
        this.observer?.disconnect();
        const more = this.querySelector('[data-load-more][data-infinite]');
        if (!more) return;
        this.observer = new IntersectionObserver(
          (entries) => entries.some((e) => e.isIntersecting) && this.loadMore(more),
          { rootMargin: '400px' }
        );
        this.observer.observe(more);
      }

      syncPriceRange(event) {
        const range = event.target.closest('[data-price-range]');
        if (!range) return;
        const [minRange, maxRange] = range.querySelectorAll('input[type="range"]');
        const [minInput, maxInput] = range.querySelectorAll('input[type="number"]');
        if (event.target === minRange || event.target === maxRange) {
          if (Number(minRange.value) > Number(maxRange.value)) {
            if (event.target === minRange) minRange.value = maxRange.value;
            else maxRange.value = minRange.value;
          }
          minInput.value = minRange.value === minRange.min ? '' : minRange.value;
          maxInput.value = maxRange.value === maxRange.max ? '' : maxRange.value;
        } else {
          minRange.value = minInput.value || minRange.min;
          maxRange.value = maxInput.value || maxRange.max;
        }
        range.style.setProperty('--from', (minRange.value - minRange.min) / (minRange.max - minRange.min || 1));
        range.style.setProperty('--to', (maxRange.value - minRange.min) / (maxRange.max - minRange.min || 1));
        // Range sliders have no name; commit through the number inputs on release.
        if (event.target.type === 'range') {
          clearTimeout(this.debounce);
          this.debounce = setTimeout(() => this.renderFromForm(), 500);
        }
      }

      setLayout(layout) {
        try {
          localStorage.setItem('rz-grid-layout', layout);
        } catch (e) {}
        this.restoreLayout();
      }

      restoreLayout() {
        let layout = 'grid';
        try {
          layout = localStorage.getItem('rz-grid-layout') || 'grid';
        } catch (e) {}
        this.querySelector('[data-facets-grid]')?.classList.toggle('product-grid--list', layout === 'list');
        this.querySelectorAll('[data-layout]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.layout === layout)));
      }
    }
  );
}
